import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

interface NavItem {
  path: string
  label: string
  icon: string
  section?: string
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: '📊' },
  { path: '/query', label: 'AI Query', icon: '🤖' },
  { path: '/markets', label: 'Markets', icon: '🌍' },
  { path: '/reports', label: 'Reports', icon: '📄' },
  { path: '/expansion-pathway', label: 'Expansion Pathway', icon: '🚀' },

  // Analytics
  { path: '/analytics/categories', label: 'Categories', icon: '📦', section: 'Analytics' },
  { path: '/analytics/quarterly', label: 'Quarterly', icon: '📅', section: 'Analytics' },
  { path: '/seller-analytics', label: 'Seller Analytics', icon: '👥', section: 'Analytics' },
  { path: '/market-comparison', label: 'Market Comparison', icon: '📈', section: 'Analytics' },

  // Compliance
  { path: '/compliance', label: 'Overview', icon: '✓', section: 'Compliance' },
  { path: '/compliance/vat', label: 'VAT & Tax', icon: '💰', section: 'Compliance' },
  { path: '/compliance-dashboard', label: 'Compliance Dashboard', icon: '📋', section: 'Compliance' },

  // Customer Experience
  { path: '/customer-journey', label: 'Customer Journey', icon: '🗺️', section: 'Customer' },
  { path: '/nps-tracking', label: 'NPS Tracking', icon: '⭐', section: 'Customer' },
  { path: '/customer-health', label: 'Customer Health', icon: '💚', section: 'Customer' },
  { path: '/churn-prediction', label: 'Churn Prediction', icon: '🔮', section: 'Customer' },
  { path: '/support-analytics', label: 'Support Analytics', icon: '🎧', section: 'Customer' },
  { path: '/customer-success', label: 'Customer Success', icon: '🏆', section: 'Customer' },

  // Revenue & Sales
  { path: '/revenue-analytics-2', label: 'Revenue Analytics', icon: '💵', section: 'Revenue & Sales' },
  { path: '/sales-pipeline', label: 'Sales Pipeline', icon: '🚰', section: 'Revenue & Sales' },
  { path: '/deal-analytics', label: 'Deal Analytics', icon: '🤝', section: 'Revenue & Sales' },
  { path: '/pricing-analytics', label: 'Pricing Analytics', icon: '🏷️', section: 'Revenue & Sales' },
  { path: '/revenue-forecasting', label: 'Revenue Forecasting', icon: '📊', section: 'Revenue & Sales' },

  // Marketing
  { path: '/campaign-analytics', label: 'Campaign Analytics', icon: '📢', section: 'Marketing' },
  { path: '/lead-scoring', label: 'Lead Scoring', icon: '🎯', section: 'Marketing' },
  { path: '/marketing-attribution', label: 'Marketing Attribution', icon: '🔗', section: 'Marketing' },
  { path: '/email-analytics', label: 'Email Analytics', icon: '📧', section: 'Marketing' },
  { path: '/seo-performance', label: 'SEO Performance', icon: '🔍', section: 'Marketing' },
  { path: '/marketing-roi', label: 'Marketing ROI', icon: '📈', section: 'Marketing' },

  // Operations
  { path: '/process-automation', label: 'Process Automation', icon: '⚙️', section: 'Operations' },
  { path: '/quality-management', label: 'Quality Management', icon: '✅', section: 'Operations' },
  { path: '/supply-chain-analytics', label: 'Supply Chain', icon: '🚚', section: 'Operations' },
  { path: '/inventory-optimization', label: 'Inventory Optimization', icon: '📦', section: 'Operations' },
  { path: '/capacity-planning', label: 'Capacity Planning', icon: '📐', section: 'Operations' },

  // Data & AI
  { path: '/data-catalog', label: 'Data Catalog', icon: '📚', section: 'Data & AI' },
  { path: '/data-quality', label: 'Data Quality', icon: '✨', section: 'Data & AI' },
  { path: '/model-registry', label: 'Model Registry', icon: '🗃️', section: 'Data & AI' },
  { path: '/model-training', label: 'Model Training', icon: '🎓', section: 'Data & AI' },
  { path: '/ml-pipeline', label: 'ML Pipeline', icon: '🔄', section: 'Data & AI' },
  { path: '/mlops-dashboard', label: 'MLOps Dashboard', icon: '🤖', section: 'Data & AI' },

  // Integration
  { path: '/api-marketplace', label: 'API Marketplace', icon: '🛒', section: 'Integration' },
  { path: '/connector-library', label: 'Connector Library', icon: '🔌', section: 'Integration' },
  { path: '/data-sync', label: 'Data Sync', icon: '🔄', section: 'Integration' },
  { path: '/etl-builder', label: 'ETL Builder', icon: '🏗️', section: 'Integration' },
  { path: '/api-analytics', label: 'API Analytics', icon: '📊', section: 'Integration' },

  // Executive
  { path: '/executive-dashboard-exec', label: 'Executive Dashboard', icon: '👔', section: 'Executive' },
  { path: '/board-reporting', label: 'Board Reporting', icon: '📊', section: 'Executive' },
  { path: '/strategic-planning-exec', label: 'Strategic Planning', icon: '🎯', section: 'Executive' },
  { path: '/kpi-management', label: 'KPI Management', icon: '📈', section: 'Executive' },
  { path: '/performance-scorecards', label: 'Performance Scorecards', icon: '📋', section: 'Executive' },
  { path: '/business-reviews', label: 'Business Reviews', icon: '📝', section: 'Executive' },
  { path: '/investment-analysis', label: 'Investment Analysis', icon: '💰', section: 'Executive' },
  { path: '/risk-dashboard', label: 'Risk Dashboard', icon: '⚠️', section: 'Executive' },
  { path: '/executive-insights', label: 'Executive Insights', icon: '💡', section: 'Executive' },

  // Tools
  { path: '/advanced-filters', label: 'Advanced Filters', icon: '🔍', section: 'Tools' },
  { path: '/bulk-export', label: 'Bulk Export', icon: '📤', section: 'Tools' },
  { path: '/widget-builder', label: 'Widget Builder', icon: '🧱', section: 'Tools' },

  // Account
  { path: '/settings', label: 'Settings', icon: '⚙️', section: 'Account' },
]

const bottomNavItems: NavItem[] = [
  { path: '/', label: 'Home', icon: '🏠' },
  { path: '/query', label: 'Query', icon: '🤖' },
  { path: '/markets', label: 'Markets', icon: '🌍' },
  { path: '/reports', label: 'Reports', icon: '📄' },
]

interface MobileNavProps {
  isOpen: boolean
  onClose: () => void
}

export const MobileNav: React.FC<MobileNavProps> = ({ isOpen, onClose }) => {
  const location = useLocation()
  const [sections, setSections] = useState<Record<string, NavItem[]>>({})

  useEffect(() => {
    // Group items by section
    const grouped: Record<string, NavItem[]> = { main: [] }
    navItems.forEach(item => {
      if (item.section) {
        if (!grouped[item.section]) {
          grouped[item.section] = []
        }
        grouped[item.section].push(item)
      } else {
        grouped.main.push(item)
      }
    })
    setSections(grouped)
  }, [])

  // Close nav on route change
  useEffect(() => {
    onClose()
  }, [location.pathname, onClose])

  // Prevent body scroll when nav is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <>
      {/* Overlay */}
      <div
        className={`mobile-nav-overlay ${isOpen ? 'active' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Side Navigation */}
      <nav className={`mobile-nav ${isOpen ? 'active' : ''}`} aria-label="Mobile navigation">
        <div className="mobile-nav-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #4A90E2 0%, #7B68EE 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '16px'
            }}>
              E
            </div>
            <span style={{ fontWeight: '600', fontSize: '18px' }}>Expandly AI</span>
          </div>
          <button className="mobile-nav-close" onClick={onClose} aria-label="Close navigation">
            ×
          </button>
        </div>

        <div className="mobile-nav-content">
          {/* Main navigation items */}
          {sections.main?.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
            >
              <span style={{ fontSize: '20px' }}>{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}

          {/* Sectioned items */}
          {Object.entries(sections).map(([sectionName, items]) => {
            if (sectionName === 'main') return null
            return (
              <div key={sectionName} className="mobile-nav-section">
                <div className="mobile-nav-section-title">{sectionName}</div>
                {items.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                  >
                    <span style={{ fontSize: '20px' }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            )
          })}
        </div>

        {/* User info at bottom */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '1rem',
          borderTop: '1px solid #E5E7EB',
          backgroundColor: '#F9FAFB'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#4A90E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '600'
            }}>
              U
            </div>
            <div>
              <div style={{ fontWeight: '500', fontSize: '14px' }}>Demo User</div>
              <div style={{ color: '#6B7280', fontSize: '12px' }}>demo@expandly.ai</div>
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

export const BottomNav: React.FC = () => {
  const location = useLocation()

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <nav className="bottom-nav safe-area-inset" aria-label="Bottom navigation">
      <div className="bottom-nav-items">
        {bottomNavItems.map(item => (
          <Link
            key={item.path}
            to={item.path}
            className={`bottom-nav-item ${isActive(item.path) ? 'active' : ''}`}
          >
            <span className="bottom-nav-icon">{item.icon}</span>
            <span className="bottom-nav-label">{item.label}</span>
          </Link>
        ))}
        <button
          className="bottom-nav-item"
          onClick={() => window.dispatchEvent(new CustomEvent('openMobileNav'))}
          style={{ background: 'none', border: 'none' }}
        >
          <span className="bottom-nav-icon">☰</span>
          <span className="bottom-nav-label">More</span>
        </button>
      </div>
    </nav>
  )
}

export const HamburgerButton: React.FC<{ isOpen: boolean; onClick: () => void }> = ({ isOpen, onClick }) => {
  return (
    <button
      className={`hamburger-btn ${isOpen ? 'active' : ''}`}
      onClick={onClick}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={isOpen}
    >
      <span />
      <span />
      <span />
    </button>
  )
}

// Hook to use responsive state
export const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)

  useEffect(() => {
    const checkSize = () => {
      setIsMobile(window.innerWidth < 768)
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024)
    }

    checkSize()
    window.addEventListener('resize', checkSize)
    return () => window.removeEventListener('resize', checkSize)
  }, [])

  return { isMobile, isTablet, isDesktop: !isMobile && !isTablet }
}
